/**
 * TourFloatingPanel — bottom-center narrator panel that overlays the real app
 * during a guided tour. Persistent across route changes because it's rendered
 * at the AppRoutes level inside TourProvider.
 *
 * Layout
 *   ┌─────────────────────────────────────────────────────┐
 *   │ avatar │ persona/route │ progress dots │ controls  │
 *   │        │ title         │               │ play|pp   │
 *   │        │ narrative…    │ ─────timer──  │ prev|next │
 *   └─────────────────────────────────────────────────────┘
 *
 * Collapsed → minimal "subtitle" strip (one-line). Click to expand.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTour } from '../../context/TourContext';
import { Play, Pause, ChevronLeft, ChevronRight, X, RotateCcw, Maximize2, Minimize2, Maximize, Minimize, HelpCircle, Volume2, VolumeX, Lightbulb, Wrench, Shield, Sparkles, Trophy, Loader2 } from 'lucide-react';

const HIGHLIGHT_KINDS = {
  tip:     { icon: Lightbulb, label: 'Tip',              accent: '#FCD34D', tint: 'rgba(252,211,77,0.12)' },
  tech:    { icon: Wrench,    label: 'Behind the scenes', accent: '#A5B4FC', tint: 'rgba(165,180,252,0.12)' },
  role:    { icon: Shield,    label: 'Access control',   accent: '#F9A8D4', tint: 'rgba(249,168,212,0.12)' },
  magic:   { icon: Sparkles,  label: 'Live magic',       accent: '#93C5FD', tint: 'rgba(147,197,253,0.14)' },
  outcome: { icon: Trophy,    label: 'Outcome',          accent: '#6EE7B7', tint: 'rgba(110,231,183,0.14)' },
};

export default function TourFloatingPanel() {
  const t = useTour();
  const [collapsed, setCollapsed] = useState(false);
  const narrativeRef = useRef(null);

  // Keep the inner narrative scroller pinned to the word the narrator is
  // currently speaking. Driven by `voice.charIndex` (fires per word via the
  // browser's `boundary` event) so the visible text follows the audio in
  // lockstep instead of feeling static.
  //
  // In BEATS mode we don't need to interpolate — each beat is its own audio
  // request, so we just scroll the active-beat paragraph fully into view
  // whenever `beatIndex` advances. Cleaner, snappier, and matches the
  // karaoke-style visual highlight on the active beat.
  useEffect(() => {
    const box = narrativeRef.current;
    if (!box) return;
    const beatsMode = Array.isArray(t.currentScene?.beats) && t.currentScene.beats.length > 0;
    if (beatsMode) {
      const active = box.querySelector(`[data-testid="tour-beat-${t.beatIndex}"]`);
      if (active) {
        try { active.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (_) { /* */ }
      }
      return;
    }
    const total = t.voice?.scriptLength || 0;
    const ci    = t.voice?.charIndex || 0;
    if (!total || !t.voice?.speaking) {
      // Reset to top on scene change / when narration starts
      box.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }
    const max  = Math.max(0, box.scrollHeight - box.clientHeight);
    if (max <= 0) return; // nothing to scroll
    const ratio = Math.min(1, ci / Math.max(1, total));
    box.scrollTo({ top: max * ratio, behavior: 'smooth' });
  }, [t.voice?.charIndex, t.voice?.scriptLength, t.voice?.speaking, t.sceneIndex, t.beatIndex, t.currentScene]);

  if (!t.isActive || !t.currentScene) return null;

  const sc = t.currentScene;
  const persona = sc.persona || {};
  const hl = sc.highlight && HIGHLIGHT_KINDS[sc.highlight.kind];
  const progressPct = Math.min(100, (t.elapsedMs / t.sceneMs) * 100);

  // Collapsed (one-line) strip
  if (collapsed) {
    return (
      <div
        data-testid="tour-panel-collapsed"
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-3 px-4 py-2.5 rounded-full bg-slate-900 border border-white/15 shadow-2xl text-white animate-fade-in"
      >
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ backgroundColor: persona.avatarColor || '#1E3A5F' }}>
          {(persona.name || '?').split(' ').map(s => s[0]).slice(0, 2).join('')}
        </div>
        <span className="text-[12px] font-medium max-w-[300px] truncate">{sc.title}</span>
        <span className="text-[10.5px] font-mono text-white/40 tabular-nums">{t.sceneIndex + 1}/{t.totalScenes}</span>
        <div className="h-[3px] w-20 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-sky-400 to-emerald-400 transition-[width] duration-100 ease-linear" style={{ width: `${progressPct}%` }} />
        </div>
        <button data-testid="tour-expand-btn" onClick={() => setCollapsed(false)} className="w-7 h-7 rounded-full border border-white/15 hover:bg-white/10 inline-flex items-center justify-center" aria-label="Expand">
          <Maximize2 className="w-3 h-3" />
        </button>
        <button data-testid="tour-exit-btn-collapsed" onClick={t.endTour} className="w-7 h-7 rounded-full border border-white/15 hover:bg-white/10 inline-flex items-center justify-center" aria-label="Exit">
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="tour-panel"
      className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[9999] w-[min(880px,calc(100vw-32px))] rounded-2xl bg-slate-900 border border-white/15 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] text-white overflow-hidden animate-fade-in"
    >
      {/* Top progress bar (within-scene) */}
      <div className="h-[3px] bg-white/5">
        <div className="h-full bg-gradient-to-r from-sky-400 to-emerald-400 transition-[width] duration-100 ease-linear" style={{ width: `${progressPct}%` }} />
      </div>

      <div className="px-5 py-4 flex items-stretch gap-4">
        {/* Persona block */}
        <div className="flex flex-col items-center gap-2 pr-4 border-r border-white/10 flex-shrink-0">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center text-[12px] font-bold shadow-lg"
            style={{ backgroundColor: persona.avatarColor || '#1E3A5F' }}
            data-testid="tour-persona-avatar"
          >
            {(persona.name || '?').split(' ').map(s => s[0]).slice(0, 2).join('')}
          </div>
          <div className="text-center min-w-[88px]">
            <div className="text-[11.5px] font-semibold leading-tight truncate text-white" data-testid="tour-persona-name">{persona.name}</div>
            <div className="text-[9.5px] uppercase tracking-wider text-slate-400">{persona.role}</div>
          </div>
          {t.transitioning && (
            <span className="inline-flex items-center gap-1 text-[9.5px] text-amber-300/90">
              <Loader2 className="w-3 h-3 animate-spin" />
              switching…
            </span>
          )}
        </div>

        {/* Narrative block */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10.5px] uppercase tracking-[0.18em] text-sky-300/90 font-semibold">Guided Tour</span>
            <span className="text-[10px] text-white/30">·</span>
            <span className="text-[10.5px] font-mono text-emerald-300/80 bg-white/[0.04] border border-white/10 rounded px-1.5 py-0.5 truncate max-w-[260px]">
              {sc.routeLabel || sc.route || '—'}
            </span>
          </div>
          <h3 className="text-[15.5px] font-semibold leading-snug mb-1.5 truncate text-white" data-testid="tour-scene-title">{sc.title}</h3>
          <div ref={narrativeRef} className="text-[13px] leading-relaxed text-slate-100 space-y-1.5 max-h-[120px] overflow-y-auto pr-1 scroll-smooth">
            {Array.isArray(sc.beats) && sc.beats.length > 0 ? (
              // Beats mode: render each beat sentence as its own paragraph.
              // The active beat is fully lit; past beats are dimmed; future
              // beats are faded further. This gives viewers a karaoke-style
              // read-along that stays in lockstep with the narrator's audio.
              sc.beats.map((b, i) => (
                <p
                  key={i}
                  data-testid={`tour-beat-${i}`}
                  data-active={i === t.beatIndex ? 'true' : 'false'}
                  className={
                    i === t.beatIndex   ? 'text-white font-medium'
                    : i <  t.beatIndex  ? 'text-white/45'
                                        : 'text-white/25'
                  }
                >
                  {b.say}
                </p>
              ))
            ) : (
              (sc.narrative || []).map((p, i) => (<p key={i}>{p}</p>))
            )}
          </div>
          {hl && (
            <div
              className="mt-2 inline-flex items-start gap-2 rounded-lg px-2.5 py-1.5 border"
              style={{ backgroundColor: hl.tint, borderColor: hl.accent + '55' }}
            >
              <hl.icon className="w-3.5 h-3.5 mt-[1px] flex-shrink-0" style={{ color: hl.accent }} />
              <span className="text-[12px] leading-snug text-slate-100">
                <strong className="font-semibold mr-1" style={{ color: hl.accent }}>{hl.label}:</strong>
                {sc.highlight.text}
              </span>
            </div>
          )}
        </div>

        {/* Controls block */}
        <div className="flex flex-col items-end justify-between gap-3 pl-3 border-l border-white/10 flex-shrink-0">
          <div className="flex items-center gap-1 text-[10.5px] font-mono text-white/55 tabular-nums">
            <span data-testid="tour-progress-index">{t.sceneIndex + 1}</span>
            <span className="text-white/30">/</span>
            <span>{t.totalScenes}</span>
          </div>
          <div className="flex items-center gap-1.5">
            {Array.from({ length: t.totalScenes }).map((_, i) => (
              <span key={i} className={`h-1 rounded-full transition-all ${i === t.sceneIndex ? 'w-4 bg-white' : i < t.sceneIndex ? 'w-2 bg-white/50' : 'w-1.5 bg-white/20'}`} />
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <button data-testid="tour-prev-btn" onClick={t.back} disabled={t.sceneIndex === 0} aria-label="Previous"
              title="Previous scene (←)"
              className="w-8 h-8 rounded-full border border-white/15 hover:bg-white/10 inline-flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button data-testid="tour-play-toggle" onClick={t.togglePlay} aria-label={t.isPlaying ? 'Pause' : 'Play'}
              title={`${t.isPlaying ? 'Pause' : 'Play'} (Space)`}
              className="w-10 h-10 rounded-full bg-white text-slate-900 hover:bg-white/90 inline-flex items-center justify-center shadow-md active:scale-95">
              {t.isPlaying ? <Pause className="w-4 h-4" fill="currentColor" /> : <Play className="w-4 h-4" fill="currentColor" />}
            </button>
            <button data-testid="tour-next-btn" onClick={t.advance} disabled={t.sceneIndex >= t.totalScenes - 1} aria-label="Next"
              title="Skip to next scene (→)"
              className="w-8 h-8 rounded-full border border-white/15 hover:bg-white/10 inline-flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              data-testid="tour-voice-toggle"
              onClick={t.voice.toggleMute}
              aria-label={t.voice.muted ? 'Unmute narrator' : 'Mute narrator'}
              title={t.voice.supported
                ? (t.voice.muted ? 'Voice narration: off' : `Voice narration: on${t.voice.voiceName ? ` (${t.voice.voiceName})` : ''}`)
                : 'Voice narration not supported in this browser'}
              disabled={!t.voice.supported}
              className={`w-7 h-7 rounded-full border inline-flex items-center justify-center disabled:opacity-30 ${
                t.voice.muted || !t.voice.supported
                  ? 'border-white/15 hover:bg-white/10 text-white/70'
                  : 'border-emerald-300/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20'
              }`}
            >
              {t.voice.muted || !t.voice.supported
                ? <VolumeX className="w-3 h-3" />
                : <Volume2 className={`w-3 h-3 ${t.voice.speaking ? 'animate-pulse' : ''}`} />}
            </button>
            <button data-testid="tour-restart-btn" onClick={t.restart} aria-label="Restart" title="Restart tour"
              className="w-7 h-7 rounded-full border border-white/15 hover:bg-white/10 inline-flex items-center justify-center">
              <RotateCcw className="w-3 h-3" />
            </button>
            <button
              data-testid="tour-fullscreen-btn"
              onClick={t.toggleFullscreen}
              aria-label={t.isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              title={`${t.isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} (F)`}
              className={`w-7 h-7 rounded-full border inline-flex items-center justify-center ${
                t.isFullscreen
                  ? 'border-sky-300/50 bg-sky-500/15 text-sky-200 hover:bg-sky-500/25'
                  : 'border-white/15 hover:bg-white/10'
              }`}
            >
              {t.isFullscreen ? <Minimize className="w-3 h-3" /> : <Maximize className="w-3 h-3" />}
            </button>
            <button
              data-testid="tour-shortcuts-btn"
              onClick={t.toggleShortcuts}
              aria-label="Keyboard shortcuts"
              title="Keyboard shortcuts (?)"
              className={`w-7 h-7 rounded-full border inline-flex items-center justify-center ${
                t.shortcutsOpen
                  ? 'border-sky-300/50 bg-sky-500/15 text-sky-200'
                  : 'border-white/15 hover:bg-white/10'
              }`}
            >
              <HelpCircle className="w-3 h-3" />
            </button>
            <button data-testid="tour-collapse-btn" onClick={() => setCollapsed(true)} aria-label="Collapse" title="Collapse panel"
              className="w-7 h-7 rounded-full border border-white/15 hover:bg-white/10 inline-flex items-center justify-center">
              <Minimize2 className="w-3 h-3" />
            </button>
            <button data-testid="tour-exit-btn" onClick={t.endTour} aria-label="Exit tour" title="Exit tour"
              className="w-7 h-7 rounded-full border border-rose-300/30 text-rose-200 hover:bg-rose-500/20 inline-flex items-center justify-center">
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
